/**
 * Human progress copy for a tool call, for the assistant activity trace.
 *
 * Why this exists
 * ---------------
 * The wire carries `display.title`, which the API builds with its own
 * `humanizeToolName()`, which produces "Skills List", "Profile Get", "Cv Tailor".
 * Mechanical noun phrases. They tell the user which function ran, not what the
 * agent is doing for them, and they carry no tense, so a running step and a
 * finished step read identically.
 *
 * This module ignores `display.title` for the status line and derives copy from
 * `toolName` + `stage` instead. That keeps every user-facing string on the client
 * where the translations live, so the SSE contract stays frozen and Arabic gets
 * reviewed like any other copy.
 *
 * Resolution order
 * ----------------
 *   1. A per-tool override, for the tools where the generic phrase reads badly
 *      ("Looking through your skills…" beats "Listing your skills…").
 *   2. A verb × entity template. `splitToolName` already gives us [scope, action]
 *      for all 89 tools, so ~11 verb groups × 4 tenses + ~20 entity nouns covers
 *      the whole registry, including tools added after this was written.
 *   3. A neutral fallback, so a brand-new tool never leaks a camelCase name.
 *
 * `display.title` is still the right thing to use *inside* a card header; it is
 * only the trace status line that this replaces.
 */
import type { TFunction } from "i18next";
import { splitToolName } from "@/lib/tool-name";
import { isSavedArtifactResult } from "@/components/artifacts/artifact-model";

/**
 * The four states a step can be in. This is also what picks its glyph: the
 * label already carries the verb ("Searching jobs…"), so giving each verb its
 * own 14px icon adds noise without adding information. The icon's one job is to
 * say whether this step is working, finished, waiting on you, or broken.
 */
export type ActivityTense = "running" | "done" | "waiting" | "failed";

export type ActivityCopy = {
  label: string;
  tense: ActivityTense;
};

/**
 * Semantic verb groups. The registry's ~30 raw actions collapse into these so
 * the copy stays natural and there are far fewer strings to translate: get /
 * view / list / load all read as "Reading…", and a tool added later that ends in
 * `Get` inherits the phrasing for free.
 */
const VERB_GROUPS = {
  search: ["search", "recommend"],
  read: [
    "get",
    "view",
    "list",
    "load",
    "graphGet",
    "runHistory",
    "unreadCount",
    "preferencesGet",
    "analyticsGet",
    "projectList",
    "projectGet",
    "artifactList",
    "artifactGet",
    "shareLinkList",
    "accessGrantList",
    "savedJobList",
    "profileBuilderGet",
    "sharedWithMeList",
    "sharedProfileGet",
  ],
  check: ["track", "compare", "compareWithMaster"],
  create: [
    "create",
    "draft",
    "duplicate",
    "compile",
    "prepare",
    "prepareRun",
    "sectionPreview",
    "projectCreate",
    "applyPrepare",
    "handoffDraft",
  ],
  update: [
    "update",
    "upsert",
    "updateSummary",
    "updateOverrides",
    "setFitToOnePage",
    "tailor",
    "markRead",
    "markAllRead",
    "preferencesUpdate",
    "projectUpdate",
    "artifactUpdate",
    "shareLinkUpdate",
    "accessGrantUpdate",
    "jobSave",
    "jobUnsave",
    "jobHide",
    "jobUnhide",
    "mute",
    "unmute",
    "skillGroupsUpdate",
    "languagesUpdate",
    "jobPreferencesUpdate",
    "privateInfoUpdate",
    "cvConfigUpdate",
    "sectionsConfigUpdate",
    "privacyUpdate",
    "profileBlocksUpdate",
    "profileSocialsUpdate",
    "profileContactsUpdate",
    "profileDesignUpdate",
    "profileSettingsUpdate",
    "sharedProfileUpdate",
    "sharedSectionUpsert",
    "sharedStructuredUpdate",
  ],
  publish: ["publish", "unpublish", "shareLinkCreate", "accessGrantCreate"],
  send: [
    "send",
    "submit",
    "scheduleInterview",
    "post",
    "trigger",
    "shortlist",
    "invoke",
    "applySubmit",
    "handoffSend",
  ],
  run: [
    "run",
    "runNow",
    "activate",
    "resume",
    "artifactGenerate",
    "artifactRegenerate",
  ],
  remove: [
    "delete",
    "close",
    "withdraw",
    "nodeDelete",
    "accessRevoke",
    "forget",
    "pause",
    "revoke",
    "artifactDelete",
    "shareLinkRevoke",
    "accessGrantRevoke",
    "sharedSectionDelete",
    "handoffCancel",
  ],
  attach: ["attach", "recall", "import", "export"],
  memory: ["add"],
} as const;

type VerbKey = keyof typeof VERB_GROUPS;

const ACTION_TO_VERB: Record<string, VerbKey> = Object.entries(
  VERB_GROUPS,
).reduce(
  (acc, [verb, actions]) => {
    for (const action of actions as readonly string[])
      acc[action] = verb as VerbKey;
    return acc;
  },
  {} as Record<string, VerbKey>,
);

/**
 * Scopes with a translated noun under `chat.activity.entity.*`.
 *
 * The nouns are singular/definite ("the job", "your CV") because the templates
 * that use them act on one thing. Every collection-shaped tool (search, list,
 * recommend) is in the override set below with its own plural phrasing, so the
 * two never have to share a noun form.
 */
const KNOWN_ENTITIES = new Set([
  "profile",
  "experience",
  "education",
  "certification",
  "project",
  "serviceOffer",
  "cv",
  "job",
  "catalogJob",
  "catalogApply",
  "application",
  "company",
  "candidate",
  "message",
  "conversation",
  "asset",
  "skill",
  "skills",
  "nova",
  "memory",
  "mcp",
  "user",
  "notification",
  "personal",
  "studio",
  "catalog",
  "messaging",
  "careerHighlight",
  "public",
]);

/**
 * Tools whose generic verb×entity phrase reads badly enough to be worth a
 * bespoke line, plus the verb-first families (connectors, memory) that
 * `splitToolName` cannot decompose into entity + action at all.
 *
 * Membership here is what lets us call `t()` on a tool key without an i18next
 * existence check: if a name is in this set, both locales define it, and the
 * i18n coverage test enforces that.
 */
const TOOL_OVERRIDES = new Set([
  // Collection-shaped
  "skillsList",
  "jobSearch",
  "catalogJobSearch",
  "jobRecommend",
  "candidateSearch",
  "assetRecall",
  "novaList",
  "cvList",
  // High-traffic, worth a bespoke phrase
  "cvGet",
  "cvTailor",
  "cvCreate",
  "cvExport",
  "cvPublish",
  "profileGet",
  "applicationPrepare",
  "catalogApplyPrepare",
  "applicationSubmit",
  "catalogApplySubmit",
  "messageDraft",
  "messageSend",
  "skillRun",
  "skillInvoke",
  "novaCompile",
  // Verb-first connector tools: splitToolName puts the verb in the scope slot
  "listConnectedServices",
  "getRecentEmails",
  "getRecentSlackMessages",
  "sendEmail",
  "postSlackMessage",
  "triggerZapierWorkflow",
  "triggerMakeScenario",
  // Verb-first memory tools
  "addMemory",
  "recallMemory",
  "forgetMemory",
  "exportMemory",
  "memoryGraphGet",
  // Research and artifacts (Phase 1): verb-first or entity-less names
  "webSearch",
  "readUrl",
  "readPdf",
  "readGithubRepo",
  "readSkill",
  "generateImage",
  "createDocument",
  "createPage",
  "artifactList",
  "artifactGet",
  // Publishing (Phase 2): verb-first names
  "publishArtifact",
  "unpublishArtifact",
  // Agent edits (Phase 2): verb-first names
  "updatePage",
  "updateDocument",
  "editImage",
  "artifactRead",
]);

/**
 * Tools that create an artifact rendered off the request path. Their call
 * completes while the file may still be `pending` (or already `failed`), so
 * the finished step reads the artifact status instead of claiming "ready".
 * `createDocument` reads as slides when its kind is `slides`.
 */
const ARTIFACT_CREATE_TOOLS = new Set([
  "generateImage",
  "createDocument",
  "createPage",
  // Agent edits (Phase 2) settle the same way: a new version, still
  // rendering (an image edit) or failed.
  "updatePage",
  "updateDocument",
  "editImage",
]);

/** Copy keys (under `chat.activity.tool`) that also define a `pending` line. */
const ARTIFACT_STATUS_COPY_KEYS = [
  "generateImage",
  "createDocument",
  "createSlides",
  "createPage",
  "editImage",
] as const;

/** Result keys that hold the collection a "found N" line should count. */
const COUNT_KEYS = [
  "skills",
  "jobs",
  "results",
  "items",
  "matches",
  "candidates",
  "applications",
  "assets",
  "messages",
  "emails",
  "conversations",
  "cvs",
  "automations",
  "nodes",
  "services",
  "artifacts",
];

/**
 * How many things a completed tool returned, for "Found {{count}} skills".
 *
 * Prefers an explicit `total` because a list endpoint's page is not its total:
 * `skillsList` returns one page of a larger catalog, and "Found 24 skills" when
 * there are more is a small lie the user can catch.
 */
export function extractActivityCount(result: unknown): number | undefined {
  if (!result || typeof result !== "object") return undefined;
  if (Array.isArray(result)) return result.length;

  const record = result as Record<string, unknown>;

  const total = record.total ?? record.count;
  if (typeof total === "number" && Number.isFinite(total)) return total;

  for (const key of COUNT_KEYS) {
    const value = record[key];
    if (Array.isArray(value)) return value.length;
  }

  return undefined;
}

function tenseFor(stage: string | undefined, isError?: boolean): ActivityTense {
  if (isError) return "failed";
  switch (stage) {
    case "completed":
      return "done";
    case "pending_approval":
      return "waiting";
    case "failed":
    case "declined":
      return "failed";
    default:
      return "running";
  }
}

function readField(value: unknown, key: string): unknown {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

/**
 * The status line of an artifact-creating tool: slides versus document from
 * the result (or the call's args while it runs), and the artifact status once
 * the call returns. A structured `{ ok: false, error }` result keeps the
 * stage-driven tense.
 */
function resolveArtifactCopy(
  toolName: string,
  tense: ActivityTense,
  result: unknown,
  args: unknown,
  t: TFunction,
): ActivityCopy {
  const kind = readField(result, "kind") ?? readField(args, "kind");
  // Suggest mode changes nothing: its own line, never "updated".
  const suggest =
    (toolName === "updatePage" || toolName === "updateDocument") &&
    (readField(args, "mode") === "suggest" ||
      readField(result, "mode") === "suggest");
  const key = suggest
    ? "suggestEdits"
    : toolName === "createDocument" && kind === "slides"
      ? "createSlides"
      : toolName === "updateDocument" && kind === "slides"
        ? "updateSlides"
        : toolName;
  const status = readField(result, "status");
  const options = { count: 0 };
  if (tense === "done" && status === "failed") {
    return {
      label: t(`chat.activity.tool.${key}.failed`, options),
      tense: "failed",
    };
  }
  if (tense === "done" && status === "pending") {
    return { label: t(`chat.activity.tool.${key}.pending`, options), tense };
  }
  return { label: t(`chat.activity.tool.${key}.${tense}`, options), tense };
}

/**
 * The status line for one tool call.
 *
 * `count` is passed to every string, not only the ones English interpolates, so
 * a translator can use `{{count}}` in a phrase where Arabic needs it and English
 * does not. `args` is optional: it only refines the running line of tools
 * whose copy depends on their input (createDocument for slides).
 */
export function resolveActivityCopy(params: {
  toolName: string;
  stage?: string;
  result?: unknown;
  args?: unknown;
  isError?: boolean;
  t: TFunction;
}): ActivityCopy {
  const { toolName, stage, result, args, isError, t } = params;
  const tense = tenseFor(stage, isError);
  const count = tense === "done" ? extractActivityCount(result) : undefined;
  const [scope, action] = splitToolName(toolName);
  const verb = ACTION_TO_VERB[action];

  if (ARTIFACT_CREATE_TOOLS.has(toolName)) {
    // A saved version is a success, whatever an older server called it: an
    // edit that kept findings the checks still flag reads "Edited the page".
    const settled = tense === "failed" && isSavedArtifactResult(result) ? "done" : tense;
    return resolveArtifactCopy(toolName, settled, result, args, t);
  }

  if (TOOL_OVERRIDES.has(toolName)) {
    return {
      label: t(`chat.activity.tool.${toolName}.${tense}`, {
        count: count ?? 0,
      }),
      tense,
    };
  }

  if (verb && KNOWN_ENTITIES.has(scope)) {
    return {
      label: t(`chat.activity.verb.${verb}.${tense}`, {
        entity: t(`chat.activity.entity.${scope}`),
        count: count ?? 0,
      }),
      tense,
    };
  }

  return {
    label: t(`chat.activity.generic.${tense}`, { count: count ?? 0 }),
    tense,
  };
}

/**
 * The two waits that aren't tool calls: before the model produces anything, and
 * between the last tool finishing and the first text token. Both are real gaps
 * the user currently stares at with no explanation.
 */
export function resolvePhaseCopy(
  phase: "thinking" | "writing",
  t: TFunction,
): ActivityCopy {
  return { label: t(`chat.activity.phase.${phase}`), tense: "running" };
}

/** Exported for the i18n coverage test, so a new override can't ship untranslated. */
export const ACTIVITY_TOOL_OVERRIDES = TOOL_OVERRIDES;
export const ACTIVITY_ARTIFACT_STATUS_COPY_KEYS = ARTIFACT_STATUS_COPY_KEYS;
/** Keys resolveArtifactCopy picks besides the tool names (every tense). */
export const ACTIVITY_ARTIFACT_EXTRA_KEYS = [
  "createSlides",
  "updateSlides",
  "suggestEdits",
] as const;
export const ACTIVITY_VERB_KEYS = Object.keys(VERB_GROUPS) as VerbKey[];
export const ACTIVITY_ENTITY_KEYS = KNOWN_ENTITIES;
